import moment from 'moment'
import { path } from 'ramda'
import AnalyticsHandler from '@/lib/analytics/handler.js'
import smartlookClient from 'smartlook-client'
import { getBaseProperties } from '@/lib/analytics/helpers.js'
import { DEFAULT_PAYLOAD, HANDLER_METHODS } from '@/lib/analytics/consts.ts'

const RAW_ACCOUNT = 'rawAccount'

export default class Smartlook extends AnalyticsHandler {
  __initialize () {
    // used to store information about all sent events
    this.handlerStack = []

    const name = this.config.name
    const code = this.config.code

    if (!name) {
      this.error('config.name not set')
      return true // do not retry
    }

    if (!code) {
      this.error('config.code not set')
      return true // do not retry
    }

    try {
      if (!smartlookClient.initialized()) {
        smartlookClient.init(this.code)
        smartlookClient.pause()
      }
    } catch (e) {
      this.error('smartlookClient error', e)
      return true // do not retry
    }

    return super.__initialize()
  }

  canBeRecored ({ email, route }) {
    const validRoute = this.isValidRoute({ route })
    const rawAccount = this.isRawAccountLoggedIn({ email })

    return validRoute || rawAccount
  }

  eventPageViewHandler (props) {
    const { data, user } = props
    const route = path(['to'], data) || {}
    const email = user ? path(['email'], user) : null

    this.setRecord({ email, route })

    this.identify(this[DEFAULT_PAYLOAD](props))

    return this.sendingSkipped
  }

  getRawAccount () {
    try {
      return JSON.parse(localStorage.getItem(RAW_ACCOUNT)) || null
    } catch (e) {
      return null
    }
  }

  isRawAccountLoggedIn ({ email }) {
    if (!email) {
      return false
    }

    const rawAccount = this.getRawAccount()

    return rawAccount?.email === email
  }

  isRawAccountExists () {
    const rawAccount = this.getRawAccount()

    return !!rawAccount?.email
  }

  isRawAccountExpired () {
    if (!this.isRawAccountExists()) {
      return true
    }

    const hoursLimit = this.config?.appConfig?.hoursLimit
    const rawAccount = this.getRawAccount()
    const registrationStartDate = moment(rawAccount?.registrationStartDate)
    const currentDate = moment()
    const duration = moment.duration(currentDate.diff(registrationStartDate))
    const expired = duration.asHours() > hoursLimit

    return expired
  }

  isValidRoute ({ route }) {
    if (!route) {
      return false
    }

    const matched = Array.isArray(route?.matched) ? route?.matched : []
    return !!matched.find(item => item?.meta?.useSmartlook)
  }

  removeRawAccount () {
    localStorage.removeItem(RAW_ACCOUNT)
  }

  send (payload) {
    if (!smartlookClient.initialized()) {
      return
    }

    const { event, ...payloadProps } = payload

    this.sendToExternalToolIfAllowed(
      () => smartlookClient.track(event, payloadProps),
      payload,
      this.handlerStack,
      HANDLER_METHODS.TRACK
    )

    this.handlerStack.push(payload)
  }

  identify (payload) {
    if (!smartlookClient.initialized()) {
      return
    }

    const { email } = payload

    // identify only users with email
    if (!email) {
      return null
    }

    this.sendToExternalToolIfAllowed(
      () => smartlookClient.identify(email, payload),
      payload,
      this.handlerStack,
      HANDLER_METHODS.IDENTIFY
    )

    this.handlerStack.push(payload)
  }

  anonymize () {
    smartlookClient.anonymize()
  }

  setRawAccount ({ email }) {
    const rawAccount = {
      email,
      registrationStartDate: new Date(),
    }

    localStorage.setItem(RAW_ACCOUNT, JSON.stringify(rawAccount))
  }

  setRecord ({ email, route, isRawEmail }) {
    this.setRecordState({ email, isRawEmail })

    if (!smartlookClient.initialized()) {
      return
    }

    if (this.canBeRecored({ email, route })) {
      this.debug(`[${this.code}] Recording resumed`)
      smartlookClient.resume()
      return
    }

    this.debug(`[${this.code}] Recording paused`)
    smartlookClient.pause()
  }

  setRecordState ({ email, isRawEmail }) {
    if (this.isRawAccountExpired()) {
      this.removeRawAccount()
    }

    if (isRawEmail && this.isRawAccountExpired()) {
      this.setRawAccount({ email })
    }
  }

  [DEFAULT_PAYLOAD] (data) {
    const baseProperties = getBaseProperties(data)

    return {
      ...baseProperties,
    }
  }
}
